import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import LegalLayout from "../components/legal/LegalLayout";

const API_BASE = process.env.REACT_APP_API_URL || "";

const VERIFICATION_TOOLTIP_COPY =
  "Verification confirms identity and license status through ClearBadge. It does not imply endorsement, quality, or suitability.";
const UNVERIFIED_TOOLTIP_COPY =
  "Not Verified means this professional has not completed ClearBadge's verification process. It does not imply lack of licensure, quality, or suitability.";

export default function InvitePage() {
  const { id } = useParams();
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState("");
  const [record, setRecord] = useState(null);

  const isTempPlaceholderId = (value) => String(value || "").startsWith("temp-");

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      if (isTempPlaceholderId(id)) {
        setRecord(null);
        setError("This invite link isn't available yet. Please share the search page or invite them directly.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_BASE}/api/placeholder/${id}`);
        if (!res.ok) {
          const text = await res.text();
          let message = text;
          try {
            const parsed = JSON.parse(text);
            message = parsed?.error || text;
          } catch (_err) {
            // ignore
          }

          if (res.status === 404) {
            setError("This invite link couldn't be found. The profile may not be created yet.");
          } else {
            setError(message || "Unable to load invite.");
          }
          setRecord(null);
          return;
        }
        setRecord(await res.json());
      } catch (err) {
        setError(err?.message || "Unable to load invite.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const displayName = useMemo(() => {
    const first = record?.firstName || "";
    const last = record?.lastName || "";
    return `${first} ${last}`.trim() || "Unclaimed profile";
  }, [record]);

  const title = id ? "Invite to Verify" : "Invite a Professional";
  const intro = id
    ? "Someone searched for you to verify your license. Claiming lets you verify your identity and license status, and manage what appears publicly."
    : "Share ClearBadge with a professional so they can claim and verify their profile.";

  return (
    <LegalLayout title={title} intro={intro} eyebrow="Invite">
      {error && <p className="error-message">{error}</p>}
      {loading && <p className="muted">Loading...</p>}

      {id && record && (
        <div className="panel" style={{ marginBottom: 14 }}>
          <h2 style={{ marginTop: 0 }}>{displayName}</h2>
          <p className="unverified-disclaimer" style={{ marginTop: 8 }}>
            {UNVERIFIED_TOOLTIP_COPY}
          </p>
          <div className="unverified-actions" style={{ marginTop: 12 }}>
            <Link to={`/claim/${id}`} className="primary claim-cta">
              Claim & Verify
            </Link>
            <Link to="/" className="ghost claim-cta">
              Search again
            </Link>
          </div>
        </div>
      )}

      {id && !loading && !record && (
        <div className="panel" style={{ marginBottom: 14 }}>
          <h2 style={{ marginTop: 0 }}>Invite a professional to verify</h2>
          <p className="unverified-disclaimer" style={{ marginTop: 8 }}>
            Share the ClearBadge search link so they can claim and verify their profile.
          </p>
          <div className="unverified-actions" style={{ marginTop: 12 }}>
            <Link to="/" className="primary claim-cta">
              Go to search
            </Link>
            <Link to="/login?mode=signup" className="ghost claim-cta">
              Get Verified
            </Link>
          </div>
        </div>
      )}

      {!id && (
        <div className="panel" style={{ marginBottom: 14 }}>
          <h2 style={{ marginTop: 0 }}>Invite a professional to verify</h2>
          <p className="unverified-disclaimer" style={{ marginTop: 8 }}>
            Share the ClearBadge search link so they can claim and verify their profile.
          </p>
          <div className="unverified-actions" style={{ marginTop: 12 }}>
            <Link to="/" className="primary claim-cta">
              Go to search
            </Link>
            <Link to="/login?mode=signup" className="ghost claim-cta">
              Get Verified
            </Link>
          </div>
        </div>
      )}

      <p className="unverified-disclaimer" role="note" aria-label="Verification disclaimer">
        {VERIFICATION_TOOLTIP_COPY}
      </p>
    </LegalLayout>
  );
}
